import {
  Chip,
  Collapse,
  Link,
  ListItemButton,
  ListItemText,
  List,
} from "@mui/material";
import { ExpandLessIcon, ExpandMoreIcon } from "components/ui/icons";
import { useSnackBar } from "contexts/SnackBarContext";
import { helpAnchor as helpAnchorProps } from "help/anchors";
import useAccountSubscription from "hooks/useAccountSubscription";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import styled from "styled-components/macro";

const Item = styled(ListItemButton)`
  padding-top: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-bottom: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-left: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 14 : 8)};
  padding-right: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 4 : 7)};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  svg {
    color: ${(props) => props.theme.palette.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
  }
  &:hover {
    background: ${(props) => props.theme.palette.sidebar.hoverBackground};
    color: ${(props) => props.theme.palette.sidebar.color};
  }
  &.active {
    background-color: ${(props) =>
      props.theme.palette.sidebar.activeBackground};
    span {
      color: ${(props) => props.theme.palette.sidebar.color};
    }
  }
`;

const Title = styled(ListItemText)`
  margin: 0;
  span {
    color: ${(props) =>
      props.depth && props.depth > 0
        ? props.theme.palette.sidebar.mutedColor
        : props.theme.palette.sidebar.color};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(4)};
  }
`;

const SidebarNavListItem = ({
  depth = 0,
  href,
  icon: Icon,
  badge,
  badgeFeature,
  title,
  helpAnchor,
  childrenItems,
}) => {
  const { openSnackbar } = useSnackBar();
  const { hasFeature } = useAccountSubscription();
  const [open, setOpen] = useState(false);
  const hasChildren = Array.isArray(childrenItems) && childrenItems.length > 0;
  const shouldShowBadge = badge && (!badgeFeature || !hasFeature(badgeFeature));

  const handleClick = () => {
    if (hasChildren) setOpen((prev) => !prev);
  };

  const handleBadgeClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    openSnackbar(
      <>
        {title} requires a Pro plan.{" "}
        <Link color="inherit" href="/plans" underline="always">
          View pricing
        </Link>
        .
      </>,
      false,
      false
    );
  };

  return (
    <>
      <Item
        component={href ? NavLink : "div"}
        to={href || undefined}
        depth={depth}
        onClick={handleClick}
        activeclassname="active"
        {...(helpAnchor ? helpAnchorProps(helpAnchor) : {})}
      >
        {Icon && <Icon size={20} />}
        <Title primary={title} depth={depth} />
        {shouldShowBadge && (
          <Chip
            color="warning"
            label={badge}
            onClick={handleBadgeClick}
            size="small"
            variant="outlined"
          />
        )}
        {hasChildren && (open ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
      </Item>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {childrenItems.map((child) => (
              <SidebarNavListItem
                key={child.title}
                depth={depth + 1}
                {...child}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export default SidebarNavListItem;
